import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useCRM } from '../context/CRMContext';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import SafeIcon from '../common/SafeIcon';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';

const {
  FiPlus, FiEdit, FiTrash2, FiShield, FiUsers, FiCheck, FiX
} = FiIcons;

const Roles = () => {
  const { isAdmin } = useAuth();
  const { roles, users, addRole, updateRole, deleteRole, loading } = useCRM();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [permissions, setPermissions] = useState({});

  // Define all available permissions in the system
  const availablePermissions = [
    { id: 'view_dashboard', name: 'View Dashboard', category: 'Dashboard' },
    { id: 'view_leads', name: 'View Leads', category: 'Leads' },
    { id: 'create_leads', name: 'Create Leads', category: 'Leads' },
    { id: 'edit_leads', name: 'Edit Leads', category: 'Leads' },
    { id: 'delete_leads', name: 'Delete Leads', category: 'Leads' },
    { id: 'convert_leads', name: 'Convert Leads to Clients', category: 'Leads' },
    { id: 'view_clients', name: 'View Clients', category: 'Clients' },
    { id: 'create_clients', name: 'Create Clients', category: 'Clients' },
    { id: 'edit_clients', name: 'Edit Clients', category: 'Clients' },
    { id: 'delete_clients', name: 'Delete Clients', category: 'Clients' },
    { id: 'view_contracts', name: 'View Contracts', category: 'Contracts' },
    { id: 'create_contracts', name: 'Create Contracts', category: 'Contracts' },
    { id: 'edit_contracts', name: 'Edit Contracts', category: 'Contracts' },
    { id: 'delete_contracts', name: 'Delete Contracts', category: 'Contracts' },
    { id: 'view_services', name: 'View Services', category: 'Services' },
    { id: 'create_services', name: 'Create Services', category: 'Services' },
    { id: 'edit_services', name: 'Edit Services', category: 'Services' },
    { id: 'delete_services', name: 'Delete Services', category: 'Services' },
    { id: 'view_works', name: 'View Works', category: 'Works' },
    { id: 'create_works', name: 'Create Works', category: 'Works' },
    { id: 'edit_works', name: 'Edit Works', category: 'Works' },
    { id: 'delete_works', name: 'Delete Works', category: 'Works' },
    { id: 'view_change_requests', name: 'View Change Requests', category: 'Change Requests' },
    { id: 'create_change_requests', name: 'Create Change Requests', category: 'Change Requests' },
    { id: 'edit_change_requests', name: 'Edit Change Requests', category: 'Change Requests' },
    { id: 'delete_change_requests', name: 'Delete Change Requests', category: 'Change Requests' },
    { id: 'view_renewals', name: 'View Renewals', category: 'Renewals' },
    { id: 'view_tasks', name: 'View Tasks', category: 'Tasks' },
    { id: 'create_tasks', name: 'Create Tasks', category: 'Tasks' },
    { id: 'edit_tasks', name: 'Edit Tasks', category: 'Tasks' },
    { id: 'delete_tasks', name: 'Delete Tasks', category: 'Tasks' },
    { id: 'view_task_reports', name: 'View Task Reports', category: 'Reports' },
    { id: 'view_user_management', name: 'View User Management', category: 'Administration' },
    { id: 'create_users', name: 'Create Users', category: 'Administration' },
    { id: 'edit_users', name: 'Edit Users', category: 'Administration' },
    { id: 'delete_users', name: 'Delete Users', category: 'Administration' },
    { id: 'view_departments', name: 'View Departments', category: 'Administration' },
    { id: 'create_departments', name: 'Create Departments', category: 'Administration' },
    { id: 'edit_departments', name: 'Edit Departments', category: 'Administration' },
    { id: 'delete_departments', name: 'Delete Departments', category: 'Administration' },
    { id: 'view_lead_sources', name: 'View Lead Sources', category: 'Administration' },
    { id: 'create_lead_sources', name: 'Create Lead Sources', category: 'Administration' },
    { id: 'edit_lead_sources', name: 'Edit Lead Sources', category: 'Administration' },
    { id: 'delete_lead_sources', name: 'Delete Lead Sources', category: 'Administration' },
    { id: 'view_settings', name: 'View Settings', category: 'Administration' },
    { id: 'edit_settings', name: 'Edit Settings', category: 'Administration' },
  ];

  // Group permissions by category
  const groupedPermissions = availablePermissions.reduce((acc, permission) => {
    if (!acc[permission.category]) {
      acc[permission.category] = [];
    }
    acc[permission.category].push(permission);
    return acc;
  }, {});

  const handleAddRole = () => {
    setEditingRole(null);
    setRoleName('');
    setRoleDescription('');
    setPermissions({});
    setIsModalOpen(true);
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
    setRoleName(role.name);
    setRoleDescription(role.description || '');
    // Parse permissions from the role
    try {
      const parsedPermissions = role.permissions ? 
        (typeof role.permissions === 'string' ? JSON.parse(role.permissions) : role.permissions) 
        : {};
      setPermissions(parsedPermissions);
    } catch (e) {
      console.error('Error parsing permissions:', e);
      setPermissions({});
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!roleName.trim()) {
      toast.error('Role name is required');
      return;
    }

    try {
      const roleData = {
        name: roleName.trim(),
        description: roleDescription.trim() || null,
        permissions: permissions // Pass as object
      };

      if (editingRole) {
        await updateRole(editingRole.id, roleData);
      } else {
        await addRole(roleData);
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving role:', error);
      if (error.code === '23505') {
        toast.error('A role with this name already exists');
      } else {
        toast.error('Failed to save role. Please check your permissions.');
      }
    }
  };

  const handleDeleteRole = async (roleId) => {
    // Check if any users are assigned to this role
    const assignedUsers = users?.filter(user => user.role_id === roleId);
    if (assignedUsers && assignedUsers.length > 0) {
      toast.error(`Cannot delete role. ${assignedUsers.length} user(s) are assigned to this role.`);
      return;
    }

    if (window.confirm('Are you sure you want to delete this role?')) {
      try {
        await deleteRole(roleId);
      } catch (error) {
        console.error('Error deleting role:', error);
        toast.error('Failed to delete role');
      }
    }
  };

  const togglePermission = (permissionId) => {
    setPermissions(prev => ({
      ...prev,
      [permissionId]: !prev[permissionId]
    }));
  };

  const toggleCategory = (category) => {
    const categoryPermissions = groupedPermissions[category];
    const allSelected = categoryPermissions.every(p => permissions[p.id]);
    
    const newPermissions = { ...permissions };
    categoryPermissions.forEach(p => {
      newPermissions[p.id] = !allSelected;
    });
    setPermissions(newPermissions);
  };

  const isCategorySelected = (category) => {
    const categoryPermissions = groupedPermissions[category];
    return categoryPermissions.every(p => permissions[p.id]);
  };

  const isCategoryPartiallySelected = (category) => {
    const categoryPermissions = groupedPermissions[category];
    const selectedCount = categoryPermissions.filter(p => permissions[p.id]).length;
    return selectedCount > 0 && selectedCount < categoryPermissions.length;
  };

  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <Card className="p-12 text-center">
          <SafeIcon icon={FiShield} className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-500">You don't have permission to manage roles.</p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading roles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Role Management</h1>
        <Button onClick={handleAddRole}>
          <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" /> Add Role
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-blue-50">
              <SafeIcon icon={FiShield} className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Roles</p>
              <p className="text-2xl font-bold text-gray-900">{roles.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-green-50">
              <SafeIcon icon={FiUsers} className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Roles</p>
              <p className="text-2xl font-bold text-gray-900">
                {roles.filter(r => r.is_active !== false).length}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-purple-50">
              <SafeIcon icon={FiUsers} className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">System Roles</p>
              <p className="text-2xl font-bold text-gray-900">
                {roles.filter(r => r.is_system).length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Roles List */}
      {roles.length === 0 ? (
        <Card className="p-12 text-center">
          <SafeIcon icon={FiShield} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No roles found</h3>
          <p className="text-gray-500 mb-4">Get started by creating your first custom role.</p>
          <Button onClick={handleAddRole}>
            <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" /> Add Role
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {roles.map((role, index) => (
            <motion.div
              key={role.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className={`p-6 hover:shadow-lg transition-shadow ${role.is_system ? 'border-l-4 border-blue-500' : ''}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {role.name}
                      {role.is_system && (
                        <span className="ml-2 text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                          System Role
                        </span>
                      )}
                    </h3>
                    {role.description && (
                      <p className="text-sm text-gray-600 mt-1">{role.description}</p>
                    )}
                  </div>
                  <div className="flex space-x-1">
                    {!role.is_system && (
                      <button
                        onClick={() => handleEditRole(role)}
                        className="text-gray-400 hover:text-primary-600 transition-colors"
                        title="Edit Role"
                      >
                        <SafeIcon icon={FiEdit} className="h-4 w-4" />
                      </button>
                    )}
                    {!role.is_system && (
                      <button
                        onClick={() => handleDeleteRole(role.id)}
                        className="text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete Role"
                      >
                        <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-500">
                    Created {new Date(role.created_at).toLocaleDateString()}
                  </p>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Add/Edit Role Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingRole ? 'Edit Role' : 'Add New Role'}
        size="xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role Name *
            </label>
            <input
              type="text"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              placeholder="e.g., Sales Manager, Developer"
              disabled={editingRole?.is_system}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={roleDescription}
              onChange={(e) => setRoleDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              placeholder="Brief description of this role"
              disabled={editingRole?.is_system}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Permissions
            </label>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              {Object.entries(groupedPermissions).map(([category, categoryPermissions]) => (
                <div key={category} className="border-b border-gray-200 last:border-b-0">
                  <div className="bg-gray-50 px-4 py-3 flex items-center">
                    <input
                      type="checkbox"
                      checked={isCategorySelected(category)}
                      ref={el => {
                        if (el) {
                          el.indeterminate = isCategoryPartiallySelected(category);
                        }
                      }}
                      onChange={() => toggleCategory(category)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      disabled={editingRole?.is_system}
                    />
                    <span className="ml-2 text-sm font-medium text-gray-900">
                      {category}
                    </span>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {categoryPermissions.map((permission) => (
                      <div key={permission.id} className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id={permission.id}
                            type="checkbox"
                            checked={!!permissions[permission.id]}
                            onChange={() => togglePermission(permission.id)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            disabled={editingRole?.is_system}
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor={permission.id} className="font-medium text-gray-700">
                            {permission.name}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setIsModalOpen(false)}
              type="button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={editingRole?.is_system}
            >
              {editingRole ? 'Update Role' : 'Create Role'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Roles;